/* =============================================================================
   nv-product.js — behaviour for the "new-variant" product template.

   Everything here is scoped to elements carrying data-nv-* hooks, which only
   exist inside the nv-* sections. Nothing binds to the document except the
   scroll/cart listeners, and those are installed once.

   Cart integration mirrors assets/product-form.js so the theme's cart drawer,
   cart icon bubble and cart-updated event keep working:
     POST window.Maximize.routes.addToCart with `sections` + `sections_url`
     -> replace the returned section HTML
     -> window.xCartHelper.updateCurrentCountItem()
     -> dispatch 'maximize:cart:updated'
     -> open #CartDrawer

   Re-runs safely on shopify:section:load so the theme editor stays live.
   ============================================================================= */
(function () {
  'use strict';

  /* Every nv-* section emits its own <script src="nv-product.js">, and identical
     external scripts each execute. Without this guard the document-level
     listeners below would be installed once per section, and each copy would
     own a separate `openDrawers` array. */
  if (window.NVProduct) return;

  var CART_UPDATED = 'maximize:cart:updated';

  /* ---------------------------------------------------------------- helpers */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  function on(el, type, fn, opts) {
    if (el) el.addEventListener(type, fn, opts);
  }

  /* Marks a node as initialised so a second init() pass is a no-op. */
  function claim(el, key) {
    var flag = 'nvInit' + key;
    if (el.dataset[flag] === '1') return false;
    el.dataset[flag] = '1';
    return true;
  }

  function routes() {
    var m = (window.Maximize && window.Maximize.routes) || {};
    var s = (window.Shopify && window.Shopify.routes) || {};
    var root = s.root || '/';
    return {
      add: m.addToCart || root + 'cart/add',
      cart: m.cart || root + 'cart',
      cartJs: root + 'cart.js'
    };
  }

  /* ------------------------------------------------------------------- cart */

  function sectionInnerHTML(html, selector) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var node = doc.querySelector(selector);
      return node ? node.innerHTML : '';
    } catch (e) {
      return '';
    }
  }

  function sectionsToRender() {
    var drawer = document.getElementById('CartDrawer');
    var id = drawer && drawer.dataset ? drawer.dataset.sectionId : null;
    if (window.xCartHelper && typeof window.xCartHelper.getSectionsToRender === 'function') {
      return window.xCartHelper.getSectionsToRender(id) || [];
    }
    return [{ id: 'cart-icon-bubble', selector: '#cart-icon-bubble' }];
  }

  function openCartDrawer() {
    var el = document.getElementById('CartDrawer');
    var enabled = !window.Maximize || window.Maximize.isEnableCartDrawer !== false;
    if (el && enabled) {
      el.isOpen = true;
      var wrap = document.getElementById('CartDrawer__Wrapper');
      if (wrap) wrap.classList.add('is-visible');
      return true;
    }
    return false;
  }

  function setBusy(btn, busy) {
    if (!btn) return;
    btn.classList.toggle('nv-is-loading', !!busy);
    btn.setAttribute('aria-busy', busy ? 'true' : 'false');
    btn.disabled = !!busy;
  }

  function showFormError(form, message) {
    var box = form ? $('[data-nv-form-error]', form) : null;
    if (!box) {
      if (message) window.alert(message);
      return;
    }
    box.textContent = message || '';
    box.hidden = !message;
    if (message) {
      clearTimeout(box._nvTimer);
      box._nvTimer = setTimeout(function () {
        box.textContent = '';
        box.hidden = true;
      }, 6000);
    }
  }

  /**
   * Adds the given form's line item to the cart.
   * @returns {Promise<boolean>} true when the item was added.
   */
  function addToCart(form, btn) {
    if (!form) return Promise.resolve(false);
    var list = sectionsToRender();
    var fd = new FormData(form);

    if (list.length) {
      fd.append('sections', list.map(function (s) { return s.id; }).join(','));
      fd.append('sections_url', window.location.pathname);
    }

    setBusy(btn, true);
    showFormError(form, '');

    return fetch(routes().add, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && (data.status === 422 || data.status === 'bad_request')) {
          showFormError(form, data.description || data.message || 'Could not add to cart.');
          return false;
        }

        if (data && data.sections) {
          list.forEach(function (s) {
            var el = document.querySelector(s.selector);
            if (el && data.sections[s.id]) {
              el.innerHTML = sectionInnerHTML(data.sections[s.id], s.selector);
            }
          });
        }

        if (window.xCartHelper && typeof window.xCartHelper.updateCurrentCountItem === 'function') {
          window.xCartHelper.updateCurrentCountItem();
        }
        document.dispatchEvent(
          new CustomEvent(CART_UPDATED, { bubbles: true, detail: { isItemCountChanged: true } })
        );
        syncCartCounts();
        return true;
      })
      .catch(function (err) {
        showFormError(form, 'Something went wrong. Please try again.');
        if (window.console) console.error('[nv-product] add to cart failed', err);
        return false;
      })
      .finally(function () {
        setBusy(btn, false);
      });
  }

  /* Keeps our own header/sticky-bar badges in step with the theme's bubble. */
  function syncCartCounts() {
    var targets = $$('[data-nv-cart-count]');
    if (!targets.length) return;

    var bubble = $('#cart-icon-bubble .cart-bubble__container');
    var fromDom = bubble && bubble.dataset ? parseInt(bubble.dataset.countItem, 10) : NaN;

    if (!isNaN(fromDom)) {
      paintCartCounts(fromDom);
      return;
    }
    fetch(routes().cartJs, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { paintCartCounts(c.item_count || 0); })
      .catch(function () {});
  }

  function paintCartCounts(count) {
    $$('[data-nv-cart-count]').forEach(function (el) {
      el.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count < 1;
    });
  }

  /* --------------------------------------------------------------- 1 gallery */

  function initGallery(root) {
    $$('[data-nv-gallery]', root).forEach(function (gal) {
      if (!claim(gal, 'Gallery')) return;

      var stage = $$('[data-nv-gallery-main] [data-nv-slide]', gal);
      var thumbs = $$('[data-nv-thumb]', gal);
      var dots = $$('[data-nv-dot]', gal);
      var rail = $('[data-nv-rail]', gal);
      var count = Math.max(stage.length, thumbs.length, rail ? rail.children.length : 0);
      var index = 0;

      function paint(next) {
        index = ((next % count) + count) % count;
        stage.forEach(function (s, i) { s.hidden = i !== index; });
        thumbs.forEach(function (t, i) {
          var active = i === index;
          t.classList.toggle('nv-border-[#121212]', active);
          t.classList.toggle('nv-border-neutral-200', !active);
          t.classList.toggle('nv-hover:border-neutral-400', !active);
          t.setAttribute('aria-current', active ? 'true' : 'false');
        });
        dots.forEach(function (d, i) {
          var active = i === index;
          d.classList.toggle('nv-bg-[#121212]', active);
          d.classList.toggle('nv-w-6', active);
          d.classList.toggle('nv-bg-neutral-300', !active);
          d.classList.toggle('nv-w-3', !active);
        });
      }

      thumbs.forEach(function (t, i) {
        on(t, 'click', function () { paint(i); });
      });
      on($('[data-nv-prev]', gal), 'click', function () { paint(index - 1); });
      on($('[data-nv-next]', gal), 'click', function () { paint(index + 1); });

      /* Mobile snap rail drives the same index (gap is 6px, per the reference). */
      if (rail) {
        on(rail, 'scroll', function () {
          var first = rail.firstElementChild;
          if (!first) return;
          var i = Math.round(rail.scrollLeft / (first.offsetWidth + 6));
          paint(Math.min(count - 1, Math.max(0, i)));
        }, { passive: true });
      }

      paint(0);
    });
  }

  /* ------------------------------------------------------------- 2 video box */

  function initVideoBox(root) {
    $$('[data-nv-video]', root).forEach(function (box) {
      if (!claim(box, 'Video')) return;
      var trigger = $('[data-nv-video-play]', box);
      var src = box.getAttribute('data-nv-video');
      if (!trigger || !src) return;

      on(trigger, 'click', function () {
        var video = document.createElement('video');
        video.src = src;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.className = 'nv-w-full nv-h-full nv-object-cover';
        trigger.replaceWith(video);
      });
    });
  }

  /* --------------------------------------------------------- 3 qty + buttons */

  function initQuantity(root) {
    $$('[data-nv-qty]', root).forEach(function (widget) {
      if (!claim(widget, 'Qty')) return;
      var input = $('[data-nv-qty-input]', widget);
      var label = $('[data-nv-qty-value]', widget);
      if (!input) return;

      function set(next) {
        var min = parseInt(input.min, 10) || 1;
        var value = Math.max(min, next);
        input.value = value;
        if (label) label.textContent = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      on($('[data-nv-qty-minus]', widget), 'click', function () {
        set((parseInt(input.value, 10) || 1) - 1);
      });
      on($('[data-nv-qty-plus]', widget), 'click', function () {
        set((parseInt(input.value, 10) || 1) + 1);
      });
      set(parseInt(input.value, 10) || 1);
    });
  }

  /* Variant <select> keeps the hidden id input, price and availability in step.
     The reference product has a single variant, so this only renders when the
     Shopify product actually has options. */
  function initVariants(root) {
    $$('[data-nv-variant-select]', root).forEach(function (select) {
      if (!claim(select, 'Variant')) return;
      var form = select.closest('[data-nv-form]');
      if (!form) return;

      var idInput = $('[data-nv-variant-id]', form);
      var priceEl = $('[data-nv-price]');
      var compareEl = $('[data-nv-compare-price]');
      var atc = $('[data-nv-atc]', form);
      var atcLabel = atc ? $('[data-nv-atc-label]', atc) : null;
      var buyNow = $('[data-nv-buynow]', form);
      var soldOut = select.getAttribute('data-nv-sold-out-label') || 'Sold out';
      var addLabel = atcLabel ? atcLabel.textContent.trim() : 'Add to Cart';

      on(select, 'change', function () {
        var opt = select.options[select.selectedIndex];
        if (!opt) return;
        var available = opt.getAttribute('data-nv-opt-available') === 'true';

        if (idInput) idInput.value = opt.value;
        if (priceEl) priceEl.textContent = opt.getAttribute('data-nv-opt-price') || '';
        if (compareEl) {
          var compare = opt.getAttribute('data-nv-opt-compare') || '';
          compareEl.textContent = compare;
          compareEl.hidden = !compare;
        }
        if (atc) atc.disabled = !available;
        if (atcLabel) atcLabel.textContent = available ? addLabel : soldOut;
        if (buyNow) buyNow.disabled = !available;
      });
    });
  }

  function initBuyButtons(root) {
    $$('[data-nv-form]', root).forEach(function (form) {
      if (!claim(form, 'Form')) return;

      on(form, 'submit', function (e) {
        e.preventDefault();
        addToCart(form, $('[data-nv-atc]', form)).then(function (ok) {
          if (ok) openCartDrawer() || (window.location.href = routes().cart);
        });
      });

      on($('[data-nv-buynow]', form), 'click', function (e) {
        e.preventDefault();
        addToCart(form, e.currentTarget).then(function (ok) {
          if (ok) window.location.href = '/checkout';
        });
      });
    });

    /* Sticky bar / compare table buttons post the hero form's current values. */
    $$('[data-nv-atc-remote]', root).forEach(function (btn) {
      if (!claim(btn, 'AtcRemote')) return;
      on(btn, 'click', function () {
        var form = $('[data-nv-form]');
        if (!form) return;
        addToCart(form, btn).then(function (ok) {
          if (ok) openCartDrawer() || (window.location.href = routes().cart);
        });
      });
    });
  }

  /* ---------------------------------------------------------- 4 coupon copy */

  function initCoupons(root) {
    $$('[data-nv-copy]', root).forEach(function (btn) {
      if (!claim(btn, 'Copy')) return;
      var code = btn.getAttribute('data-nv-copy');
      var idle = $('[data-nv-copy-idle]', btn);
      var done = $('[data-nv-copy-done]', btn);

      on(btn, 'click', function () {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).catch(function () {});
        }
        if (idle) idle.hidden = true;
        if (done) done.hidden = false;
        clearTimeout(btn._nvTimer);
        btn._nvTimer = setTimeout(function () {
          if (idle) idle.hidden = false;
          if (done) done.hidden = true;
        }, 2000);
      });
    });
  }

  /* ---------------------------------------------------------------- 5 tabs */

  function initTabs(root) {
    $$('[data-nv-tabs]', root).forEach(function (group) {
      if (!claim(group, 'Tabs')) return;
      var tabs = $$('[data-nv-tab]', group);
      var panels = $$('[data-nv-panel]', group);

      function select(id) {
        tabs.forEach(function (t) {
          var active = t.getAttribute('data-nv-tab') === id;
          t.setAttribute('aria-selected', active ? 'true' : 'false');
          t.classList.toggle('nv-text-[#121212]', active);
          t.classList.toggle('nv-text-neutral-500', !active);
          t.classList.toggle('nv-hover:text-neutral-700', !active);
          var underline = $('[data-nv-tab-underline]', t);
          if (underline) underline.hidden = !active;
        });
        panels.forEach(function (p) {
          p.hidden = p.getAttribute('data-nv-panel') !== id;
        });
      }

      tabs.forEach(function (t) {
        on(t, 'click', function () { select(t.getAttribute('data-nv-tab')); });
      });
      if (tabs.length) select(tabs[0].getAttribute('data-nv-tab'));
    });
  }

  /* -------------------------------------------------- 6 before/after slider */

  function initBeforeAfter(root) {
    $$('[data-nv-ba]', root).forEach(function (frame) {
      if (!claim(frame, 'Ba')) return;
      var input = $('[data-nv-ba-input]', frame);
      var clip = $('[data-nv-ba-clip]', frame);
      var handle = $('[data-nv-ba-handle]', frame);
      if (!input) return;

      function paint() {
        var pct = Number(input.value);
        if (clip) clip.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
        if (handle) handle.style.left = pct + '%';
      }
      on(input, 'input', paint);
      on(input, 'change', paint);
      paint();
    });
  }

  /* ----------------------------------------------------------- 7 accordions */

  function initAccordions(root) {
    $$('[data-nv-accordion]', root).forEach(function (group) {
      if (!claim(group, 'Accordion')) return;
      var items = $$('[data-nv-acc-item]', group);

      function paint(openIndex) {
        items.forEach(function (item, i) {
          var open = i === openIndex;
          var head = $('[data-nv-acc-head]', item);
          var body = $('[data-nv-acc-body]', item);
          var chev = $('[data-nv-acc-chevron]', item);
          if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (body) {
            body.style.gridTemplateRows = open ? '1fr' : '0fr';
            body.setAttribute('aria-hidden', open ? 'false' : 'true');
          }
          if (chev) chev.classList.toggle('nv-rotate-180', open);
        });
      }

      items.forEach(function (item, i) {
        var head = $('[data-nv-acc-head]', item);
        on(head, 'click', function () {
          var isOpen = head.getAttribute('aria-expanded') === 'true';
          paint(isOpen ? -1 : i);
        });
      });

      var initial = parseInt(group.getAttribute('data-nv-accordion-open'), 10);
      paint(isNaN(initial) ? -1 : initial);
    });
  }

  /* ------------------------------------------------ 8 compatibility checker */

  function initCompatibility(root) {
    $$('[data-nv-compat]', root).forEach(function (widget) {
      if (!claim(widget, 'Compat')) return;
      var chips = $$('[data-nv-compat-chip]', widget);
      var panel = $('[data-nv-compat-panel]', widget);
      if (!chips.length || !panel) return;

      var nameEl = $('[data-nv-compat-name]', panel);
      var verdictEl = $('[data-nv-compat-verdict]', panel);
      var noteEl = $('[data-nv-compat-note]', panel);
      var okIcon = $('[data-nv-compat-icon-ok]', panel);
      var noIcon = $('[data-nv-compat-icon-no]', panel);
      var circle = $('[data-nv-compat-circle]', panel);

      function select(i) {
        chips.forEach(function (chip, n) {
          var active = n === i;
          chip.setAttribute('aria-selected', active ? 'true' : 'false');
          var ok = chip.getAttribute('data-nv-compat-ok') === 'true';
          $$('[data-nv-compat-badge]', chip).forEach(function (b) {
            b.hidden = b.getAttribute('data-nv-compat-badge') !==
              (active ? (ok ? 'ok' : 'no') : 'idle');
          });
          var label = $('[data-nv-compat-label]', chip);
          if (label) {
            label.classList.toggle('nv-bg-[#121212]', active);
            label.classList.toggle('nv-text-white', active);
          }
        });

        var chip = chips[i];
        if (!chip) return;
        var ok = chip.getAttribute('data-nv-compat-ok') === 'true';

        if (nameEl) nameEl.textContent = chip.getAttribute('data-nv-compat-title') || '';
        if (verdictEl) {
          verdictEl.textContent = chip.getAttribute('data-nv-compat-verdict') || '';
          verdictEl.classList.toggle('nv-text-green-700', ok);
          verdictEl.classList.toggle('nv-text-amber-700', !ok);
        }
        if (noteEl) noteEl.textContent = chip.getAttribute('data-nv-compat-note') || '';
        if (okIcon) okIcon.hidden = !ok;
        if (noIcon) noIcon.hidden = ok;
        if (circle) {
          circle.classList.toggle('nv-bg-green-50', ok);
          circle.classList.toggle('nv-text-green-700', ok);
          circle.classList.toggle('nv-border-green-200', ok);
          circle.classList.toggle('nv-bg-amber-50', !ok);
          circle.classList.toggle('nv-text-amber-700', !ok);
          circle.classList.toggle('nv-border-amber-200', !ok);
        }
        panel.classList.toggle('nv-border-l-green-600', ok);
        panel.classList.toggle('nv-border-l-amber-500', !ok);
      }

      chips.forEach(function (chip, i) {
        on(chip, 'click', function () { select(i); });
      });
      select(0);
    });
  }

  /* -------------------------------------------------------------- 9 drawers */

  var openDrawers = [];

  function lockScroll(lock) {
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  function setDrawer(drawer, open) {
    var panel = $('[data-nv-drawer-panel]', drawer);
    var scrim = $('[data-nv-drawer-scrim]', drawer);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (panel) {
      panel.classList.toggle('nv-translate-x-0', open);
      panel.classList.toggle('nv-translate-x-full', !open);
    }
    if (scrim) {
      scrim.classList.toggle('nv-opacity-100', open);
      scrim.classList.toggle('nv-opacity-0', !open);
      scrim.classList.toggle('nv-pointer-events-none', !open);
    }
    var i = openDrawers.indexOf(drawer);
    if (open && i < 0) openDrawers.push(drawer);
    if (!open && i > -1) openDrawers.splice(i, 1);
    lockScroll(openDrawers.length > 0);
  }

  function initDrawers(root) {
    $$('[data-nv-drawer]', root).forEach(function (drawer) {
      if (!claim(drawer, 'Drawer')) return;
      setDrawer(drawer, false);
      $$('[data-nv-drawer-close]', drawer).forEach(function (btn) {
        on(btn, 'click', function () { setDrawer(drawer, false); });
      });
      on($('[data-nv-drawer-scrim]', drawer), 'click', function () {
        setDrawer(drawer, false);
      });
    });

    $$('[data-nv-drawer-open]', root).forEach(function (btn) {
      if (!claim(btn, 'DrawerOpen')) return;
      on(btn, 'click', function () {
        var target = document.querySelector(
          '[data-nv-drawer="' + btn.getAttribute('data-nv-drawer-open') + '"]'
        );
        if (target) setDrawer(target, true);
      });
    });
  }

  /* ------------------------------------------------------- 10 compare drawer */

  function initCompare(root) {
    $$('[data-nv-compare]', root).forEach(function (widget) {
      if (!claim(widget, 'Compare')) return;

      var rows = $$('[data-nv-compare-row]', widget);
      var slots = $$('[data-nv-compare-slot]', widget);
      var pickerView = $('[data-nv-compare-picker]', widget);
      var tableView = $('[data-nv-compare-table]', widget);
      var goBtn = $('[data-nv-compare-go]', widget);
      var backBtn = $('[data-nv-compare-back]', widget);
      var selected = [];
      var MAX = 3;

      function paint() {
        rows.forEach(function (row) {
          var id = row.getAttribute('data-nv-compare-row');
          var isOn = selected.indexOf(id) > -1;
          row.classList.toggle('nv-border-[#121212]', isOn);
          row.classList.toggle('nv-bg-[#faf9f7]', isOn);
          row.classList.toggle('nv-border-neutral-200', !isOn);
          row.setAttribute('aria-pressed', isOn ? 'true' : 'false');
          var box = $('[data-nv-compare-check]', row);
          if (box) {
            box.classList.toggle('nv-bg-[#121212]', isOn);
            box.classList.toggle('nv-border-[#121212]', isOn);
            box.classList.toggle('nv-text-white', isOn);
            box.classList.toggle('nv-border-neutral-300', !isOn);
            box.classList.toggle('nv-text-transparent', !isOn);
          }
        });

        slots.forEach(function (slot, i) {
          var id = selected[i];
          var filled = $('[data-nv-compare-slot-filled]', slot);
          var empty = $('[data-nv-compare-slot-empty]', slot);
          if (filled) {
            filled.hidden = !id;
            if (id) {
              var src = document.querySelector('[data-nv-compare-row="' + id + '"]');
              var img = $('[data-nv-compare-slot-img]', filled);
              var name = $('[data-nv-compare-slot-name]', filled);
              var thumb = src ? $('[data-nv-compare-row-img]', src) : null;
              if (img && thumb) img.src = thumb.src;
              if (name && src) name.textContent = src.getAttribute('data-nv-compare-name') || '';
              filled.setAttribute('data-nv-current', id);
            }
          }
          if (empty) empty.hidden = !!id;
        });

        $$('[data-nv-compare-col]', tableView).forEach(function (col) {
          var id = col.getAttribute('data-nv-compare-col');
          col.hidden = id !== 'this' && selected.indexOf(id) < 0;
        });

        if (goBtn) {
          goBtn.disabled = selected.length === 0;
          goBtn.classList.toggle('nv-bg-[#121212]', selected.length > 0);
          goBtn.classList.toggle('nv-text-white', selected.length > 0);
          goBtn.classList.toggle('nv-bg-neutral-200', selected.length === 0);
          goBtn.classList.toggle('nv-text-neutral-500', selected.length === 0);
          goBtn.classList.toggle('nv-cursor-not-allowed', selected.length === 0);
          var count = $('[data-nv-compare-count]', goBtn);
          if (count) count.textContent = String(selected.length);
        }
      }

      rows.forEach(function (row) {
        on(row, 'click', function () {
          var id = row.getAttribute('data-nv-compare-row');
          var i = selected.indexOf(id);
          if (i > -1) selected.splice(i, 1);
          else if (selected.length < MAX) selected.push(id);
          paint();
        });
      });

      slots.forEach(function (slot) {
        on($('[data-nv-compare-slot-remove]', slot), 'click', function (e) {
          e.stopPropagation();
          var filled = $('[data-nv-compare-slot-filled]', slot);
          var id = filled ? filled.getAttribute('data-nv-current') : null;
          var i = selected.indexOf(id);
          if (i > -1) selected.splice(i, 1);
          paint();
        });
      });

      function view(showTable) {
        if (pickerView) pickerView.hidden = showTable;
        if (tableView) tableView.hidden = !showTable;
      }
      on(goBtn, 'click', function () { if (selected.length) view(true); });
      on(backBtn, 'click', function () { view(false); });

      view(false);
      paint();
    });
  }

  /* ---------------------------------------------------------------- 11 quiz */

  function initQuiz(root) {
    $$('[data-nv-quiz]', root).forEach(function (quiz) {
      if (!claim(quiz, 'Quiz')) return;

      var steps = $$('[data-nv-quiz-step]', quiz);
      var result = $('[data-nv-quiz-result]', quiz);
      var bars = $$('[data-nv-quiz-bar]', quiz);
      var counter = $('[data-nv-quiz-counter]', quiz);
      var counterTpl = counter ? counter.getAttribute('data-nv-quiz-counter') || '' : '';
      var answers = [];
      var step = 0;

      function paint() {
        var finished = step >= steps.length;
        steps.forEach(function (s, i) { s.hidden = finished || i !== step; });
        if (result) result.hidden = !finished;
        bars.forEach(function (b, i) {
          b.classList.toggle('nv-bg-[#121212]', i <= step);
          b.classList.toggle('nv-bg-neutral-200', i > step);
        });
        if (counter) {
          counter.hidden = finished;
          counter.textContent = counterTpl
            .replace('[step]', String(step + 1))
            .replace('[total]', String(steps.length));
        }
        if (finished && result) {
          /* "Never" as the retinoid-experience answer downgrades the verdict. */
          var beginner = (answers[2] || '').toLowerCase().indexOf('never') === 0;
          $$('[data-nv-quiz-verdict]', result).forEach(function (v) {
            v.hidden = v.getAttribute('data-nv-quiz-verdict') !== (beginner ? 'beginner' : 'match');
          });
        }
      }

      $$('[data-nv-quiz-option]', quiz).forEach(function (opt) {
        on(opt, 'click', function () {
          answers.push(opt.getAttribute('data-nv-quiz-option') || opt.textContent.trim());
          setTimeout(function () { step += 1; paint(); }, 250);
        });
      });

      on($('[data-nv-quiz-retake]', quiz), 'click', function () {
        answers = [];
        step = 0;
        paint();
      });

      /* Bundle toggle swaps the CTA label/price. */
      var bundle = $('[data-nv-quiz-bundle]', quiz);
      on(bundle, 'change', function () {
        $$('[data-nv-quiz-cta-label]', quiz).forEach(function (el) {
          el.hidden = (el.getAttribute('data-nv-quiz-cta-label') === 'bundle') !== bundle.checked;
        });
      });
      if (bundle) bundle.dispatchEvent(new Event('change'));

      paint();
    });
  }

  /* ------------------------------------------------------- 12 sticky ATC bar */

  function initStickyBar(root) {
    $$('[data-nv-sticky-atc]', root).forEach(function (bar) {
      if (!claim(bar, 'Sticky')) return;
      var threshold = parseInt(bar.getAttribute('data-nv-sticky-atc'), 10) || 700;

      function paint() {
        var show = window.scrollY > threshold;
        bar.classList.toggle('nv-translate-y-0', show);
        bar.classList.toggle('nv-translate-y-full', !show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      }
      on(window, 'scroll', paint, { passive: true });
      paint();
    });
  }

  /* ------------------------------------------------------------ 13 wishlist */

  function initWishlist(root) {
    var KEY = 'nv-wishlist';
    var buttons = $$('[data-nv-wishlist]', root);
    if (!buttons.length) return;

    function read() {
      try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; }
    }
    function paint(on_) {
      $$('[data-nv-wishlist]').forEach(function (btn) {
        btn.setAttribute('aria-pressed', on_ ? 'true' : 'false');
        btn.setAttribute('aria-label', on_ ? 'Remove from wishlist' : 'Add to wishlist');
        btn.classList.toggle('nv-border-[#121212]', on_);
        btn.classList.toggle('nv-bg-[#121212]', on_);
        btn.classList.toggle('nv-border-neutral-300', !on_);
        var icon = $('svg', btn);
        if (icon) {
          icon.classList.toggle('nv-fill-white', on_);
          icon.classList.toggle('nv-text-white', on_);
          icon.classList.toggle('nv-text-[#121212]', !on_);
        }
      });
      $$('[data-nv-wishlist-saved]').forEach(function (el) { el.hidden = !on_; });
      $$('[data-nv-wishlist-count]').forEach(function (el) {
        el.textContent = on_ ? '1' : '0';
        el.hidden = !on_;
      });
    }

    buttons.forEach(function (btn) {
      if (!claim(btn, 'Wish')) return;
      on(btn, 'click', function () {
        var next = !read();
        try { localStorage.setItem(KEY, next ? '1' : '0'); } catch (e) {}
        paint(next);
      });
    });
    paint(read());
  }

  /* ------------------------------------------------------- 14 smooth anchors */

  function initAnchors(root) {
    $$('[data-nv-scroll-to]', root).forEach(function (link) {
      if (!claim(link, 'Anchor')) return;
      on(link, 'click', function (e) {
        var id = link.getAttribute('data-nv-scroll-to');
        var target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* --------------------------------------------------------------- 15 header */

  function initHeader(root) {
    $$('[data-nv-header]', root).forEach(function (header) {
      if (!claim(header, 'Header')) return;

      /* Cart button hands off to the theme's drawer, falling back to /cart. */
      on($('[data-nv-cart-open]', header), 'click', function () {
        if (!openCartDrawer()) window.location.href = routes().cart;
      });

      var menu = $('[data-nv-menu-panel]', header);
      $$('[data-nv-menu-toggle]', header).forEach(function (btn) {
        on(btn, 'click', function () {
          var open = menu.hasAttribute('hidden');
          if (open) menu.removeAttribute('hidden');
          else menu.setAttribute('hidden', '');
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      });

      var search = $('[data-nv-search-panel]', header);
      $$('[data-nv-search-toggle]', header).forEach(function (btn) {
        on(btn, 'click', function () {
          var open = search.hasAttribute('hidden');
          if (open) {
            search.removeAttribute('hidden');
            var field = $('input[type="search"]', search);
            if (field) field.focus();
          } else {
            search.setAttribute('hidden', '');
          }
          btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      });
    });
  }

  /* ------------------------------------------------------------------- boot */

  function init(root) {
    var scope = root || document;
    initHeader(scope);
    initGallery(scope);
    initVideoBox(scope);
    initQuantity(scope);
    initVariants(scope);
    initBuyButtons(scope);
    initCoupons(scope);
    initTabs(scope);
    initBeforeAfter(scope);
    initAccordions(scope);
    initCompatibility(scope);
    initDrawers(scope);
    initCompare(scope);
    initQuiz(scope);
    initStickyBar(scope);
    initWishlist(scope);
    initAnchors(scope);
    syncCartCounts();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor re-renders a single section: re-init just that subtree. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  /* Escape closes the topmost drawer. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !openDrawers.length) return;
    setDrawer(openDrawers[openDrawers.length - 1], false);
  });

  /* Any other part of the theme changing the cart re-syncs our badges. */
  document.addEventListener(CART_UPDATED, function () { syncCartCounts(); });

  window.NVProduct = { init: init, addToCart: addToCart, openCartDrawer: openCartDrawer };
})();
